import React, { memo, useMemo } from 'react';
import cx from 'classnames';

import {
  OpportunityAiSummary,
  OpportunityLabels,
  PostedByInfo,
  CandidatePreferences,
  OpportunityApplicationBanner,
} from 'components/opportunity-page';
import { EventApplicants } from 'components/ComponentV2';
import { TouchpointType } from 'lib/models/touchpoint';
import { parseResponse } from 'lib/utils/parser';
import { EventTouchpointable } from 'lib/models/event';
import { useOpportunity } from 'lib/providers/OpportunityProvider';

import styles from './OpportunityHead.module.scss';

type OpportunityHeadProps = {
  className?: string;
};

export const OpportunityHead: React.FC<OpportunityHeadProps> = memo(({ className = '' }: OpportunityHeadProps) => {
  const {
    opportunity: { title, ai_summary, touchpointable: touchpointableResponse, touchpointable_type, applications, avatar_urls, show_guest_list },
    isOpportunityAppliedOrUnappliable,
    isSearchPage,
  } = useOpportunity();

  const bannerURL = useMemo(() => {
    if (touchpointable_type !== TouchpointType.Event) return;

    const touchpointable = parseResponse(touchpointableResponse as EventTouchpointable);
    return touchpointable.icon_url;
  }, [touchpointableResponse, touchpointable_type]);

  const get_applicants_row = useMemo(() => {
    return (
      touchpointable_type === TouchpointType.Event && (
        <EventApplicants application_count={applications} avatar_urls={avatar_urls} className={styles.applicants} />
      )
    );
  }, [touchpointable_type, applications, avatar_urls?.length]);

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles['basic-info']}>
        {!isSearchPage && bannerURL && <img src={bannerURL} className={styles.image} />}
        <h1 className={styles.title}>{title}</h1>
        <PostedByInfo className={styles['posted-by-info']} />
      </div>
      <OpportunityApplicationBanner />
      <OpportunityLabels className={styles.labels} />
      {!isOpportunityAppliedOrUnappliable && !!ai_summary?.length && (
        <div className={styles['not-applied-section']}>
          <OpportunityAiSummary ai_summary={ai_summary} />
          <CandidatePreferences />
        </div>
      )}
      {show_guest_list && get_applicants_row}
    </div>
  );
});

OpportunityHead.displayName = 'OpportunityHead';
